import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import ProductCard from '../components/ProductCard';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRODUCTS_PER_PAGE = 6;

export default function ShopPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  const selectedCategory = searchParams.get('category') || '';
  const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);

  // Horizontal scroll for the category filter bar
  const filterScrollRef = useRef(null);
  const scrollFilterBar = (delta) => {
    if (filterScrollRef.current) {
      filterScrollRef.current.scrollBy({ left: delta, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/categories`),
        ]);
        setProducts(productsRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleCategoryChange = (category) => {
    if (!category) {
      searchParams.delete('category');
    } else {
      searchParams.set('category', category);
    }
    searchParams.delete('page');
    setSearchParams(searchParams);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageChange = (page) => {
    searchParams.set('page', String(page));
    setSearchParams(searchParams);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filteredProducts = useMemo(() => {
    if (!selectedCategory || selectedCategory === 'all') return products;
    return products.filter((p) => p.category === selectedCategory);
  }, [products, selectedCategory]);

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedProducts = useMemo(() => {
    const start = (safePage - 1) * PRODUCTS_PER_PAGE;
    return filteredProducts.slice(start, start + PRODUCTS_PER_PAGE);
  }, [filteredProducts, safePage]);

  // Build category cards with cover image (fallback to first product image) and product count
  const categoryCards = useMemo(() => {
    return categories.map((cat) => {
      const productsInCat = products.filter((p) => p.category === cat.id);
      const explicitCover = cat.image_url || cat.image || cat.cover_image;
      const firstProductImg = productsInCat[0]?.image;
      const cover = explicitCover || firstProductImg || null;
      return {
        ...cat,
        cover,
        product_count: productsInCat.length,
      };
    });
  }, [categories, products]);

  const handleQuickAdd = async (product) => {
    try {
      await addToCart({ product_id: product.id, quantity: 1 });
      toast.success(`${product.name} added to cart!`, {
        action: { label: 'View Cart', onClick: () => navigate('/cart') },
        duration: 5000,
        style: { cursor: 'pointer' },
        onClick: () => navigate('/cart'),
      });
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  const showCategoriesGrid = !selectedCategory;
  const activeCategory = categories.find((c) => c.id === selectedCategory);

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="shop-page">
      <SEO
        title={activeCategory ? `${activeCategory.name} - Order Online in Dublin` : "Shop Custom Cakes Dublin - Birthday, Communion, Bento & More"}
        description={activeCategory
          ? `Order ${activeCategory.name.toLowerCase()} online from Mad Brothers Bakery in Dublin 7. Handcrafted with 30+ years of family tradition. Collection only.`
          : "Browse our full range of celebration cakes, communion cakes, fresh cream cakes, bento cakes, cupcake boxes and artisan bread. Order online for collection in Dublin 7."}
        keywords={activeCategory
          ? `${activeCategory.name} Dublin, ${activeCategory.name} Ireland, order ${activeCategory.name.toLowerCase()} online, custom ${activeCategory.name.toLowerCase()}`
          : "shop cakes Dublin, order cake online Dublin, birthday cakes Dublin, communion cakes Dublin, bento cakes Dublin, cupcake boxes Dublin, artisan bread Dublin"}
      />
      {/* Header */}
      <section className="bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {showCategoriesGrid ? (
              <>
                <h1 className="font-['Playfair_Display'] text-3xl sm:text-5xl font-bold text-[#1a1a1a] tracking-tight">
                  Shop by Category
                </h1>
                <p className="text-stone-600 text-sm sm:text-base mt-2 sm:mt-3 max-w-2xl">
                  Explore our collections of celebration cakes, cupcakes, artisan bread and seasonal specials —
                  handcrafted with 30+ years of family tradition.
                </p>
              </>
            ) : (
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => handleCategoryChange('')}
                  className="self-start inline-flex items-center gap-2 text-stone-600 hover:text-[#1a1a1a] text-sm font-medium transition-colors"
                  data-testid="back-to-categories-btn"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Categories
                </button>
                <h1 className="font-['Playfair_Display'] text-3xl sm:text-5xl font-bold text-[#1a1a1a] tracking-tight">
                  {selectedCategory === 'all'
                    ? 'All Products'
                    : activeCategory?.name || 'Shop'}
                </h1>
                {activeCategory?.description && (
                  <p className="text-stone-600 text-sm sm:text-base max-w-2xl">
                    {activeCategory.description}
                  </p>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* CATEGORIES GRID VIEW */}
      {showCategoriesGrid ? (
        <section className="py-8 sm:py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-7">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div
                    key={i}
                    className="aspect-[3/4] bg-stone-200 rounded-2xl animate-pulse"
                  />
                ))}
              </div>
            ) : (
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-7"
                data-testid="categories-grid"
              >
                {categoryCards.map((cat, idx) => (
                  <motion.button
                    key={cat.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, delay: idx * 0.05 }}
                    onClick={() => handleCategoryChange(cat.id)}
                    className="group relative aspect-[3/4] overflow-hidden rounded-2xl bg-gradient-to-br from-stone-100 to-stone-200 shadow-sm hover:shadow-2xl transition-all duration-500 text-left"
                    data-testid={`category-card-${cat.id}`}
                  >
                    {/* Cover image or elegant placeholder */}
                    {cat.cover ? (
                      <img
                        src={cat.cover}
                        alt={cat.name}
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#1a1a1a] via-[#2a2a2a] to-[#1a1a1a]">
                        <span className="font-['Playfair_Display'] text-7xl text-[#D4AF37]/40 italic">
                          {cat.name?.[0] || '?'}
                        </span>
                      </div>
                    )}

                    {/* Dark gradient for legibility */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />

                    {/* Product count badge */}
                    <div className="absolute top-4 right-4 z-10">
                      <span
                        className="inline-flex items-center px-3 py-1 rounded-full bg-white/90 backdrop-blur-sm text-[#1a1a1a] text-xs font-semibold tracking-wide"
                        data-testid={`category-count-${cat.id}`}
                      >
                        {cat.product_count}{' '}
                        {cat.product_count === 1 ? 'product' : 'products'}
                      </span>
                    </div>

                    {/* Bottom overlay: Name + CTA */}
                    <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7">
                      <h3 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-white leading-tight tracking-tight">
                        {cat.name}
                      </h3>
                      {cat.description && (
                        <p className="mt-1.5 text-white/80 text-sm line-clamp-2">
                          {cat.description}
                        </p>
                      )}
                      <span className="mt-3 inline-flex items-center gap-2 text-[#D4AF37] text-sm font-semibold tracking-wider uppercase opacity-90 group-hover:gap-3 transition-all">
                        Shop now
                        <span aria-hidden>→</span>
                      </span>
                    </div>
                  </motion.button>
                ))}

                {/* All Products tile */}
                <motion.button
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.45,
                    delay: categoryCards.length * 0.05,
                  }}
                  onClick={() => handleCategoryChange('all')}
                  className="group relative aspect-[3/4] overflow-hidden rounded-2xl bg-[#1a1a1a] hover:bg-black shadow-sm hover:shadow-2xl transition-all duration-500 text-left flex items-center justify-center"
                  data-testid="category-card-all"
                >
                  <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_30%_20%,#D4AF37_0%,transparent_50%)]" />
                  <div className="relative z-10 text-center px-6">
                    <p className="font-['Cormorant_Garamond'] text-[#D4AF37] text-xs sm:text-sm font-bold tracking-[0.3em] uppercase mb-3">
                      Browse Everything
                    </p>
                    <h3 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-white leading-tight">
                      All Products
                    </h3>
                    <p className="mt-3 text-stone-400 text-sm">
                      {products.length} items in total
                    </p>
                    <span className="mt-5 inline-flex items-center gap-2 text-[#D4AF37] text-sm font-semibold tracking-wider uppercase group-hover:gap-3 transition-all">
                      Explore
                      <span aria-hidden>→</span>
                    </span>
                  </div>
                </motion.button>
              </div>
            )}
          </div>
        </section>
      ) : (
        <>
          {/* Quick category switcher (visible when viewing a category) */}
          <section className="bg-gradient-to-b from-[#1a1a1a] to-[#2a2a2a] sticky top-[72px] sm:top-[80px] z-40 shadow-lg">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 relative">
              <div
                ref={filterScrollRef}
                className="flex items-center gap-2 sm:gap-3 overflow-x-auto scrollbar-hide pb-1 pr-12 pl-12 sm:px-12 scroll-smooth"
              >
                <button
                  onClick={() => handleCategoryChange('all')}
                  className={`px-4 sm:px-6 py-2.5 rounded-full whitespace-nowrap font-['Cormorant_Garamond'] text-[15px] sm:text-[17px] font-bold transition-all duration-300 flex-shrink-0 ${
                    selectedCategory === 'all'
                      ? 'bg-white text-[#1a1a1a] shadow-lg'
                      : 'bg-transparent text-white/80 hover:text-white border-2 border-white/40 hover:border-white/70'
                  }`}
                  data-testid="filter-all"
                >
                  All Products
                </button>
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryChange(category.id)}
                    className={`relative px-4 sm:px-6 py-2.5 rounded-full whitespace-nowrap font-['Cormorant_Garamond'] text-[15px] sm:text-[17px] font-bold transition-all duration-300 flex-shrink-0 ${
                      selectedCategory === category.id
                        ? 'bg-[#D4AF37] text-[#1a1a1a] shadow-[0_0_20px_rgba(212,175,55,0.5)]'
                        : 'bg-transparent text-white hover:text-[#D4AF37] border-2 border-[#D4AF37]/50 hover:border-[#D4AF37]'
                    }`}
                    data-testid={`filter-${category.id}`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>

              {/* Left scroll arrow */}
              <button
                type="button"
                onClick={() => scrollFilterBar(-280)}
                className="absolute left-1 sm:left-2 top-1/2 -translate-y-1/2 h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-white/10 backdrop-blur-md text-white hover:bg-white hover:text-[#1a1a1a] border border-white/20 hover:border-white shadow-lg flex items-center justify-center transition-all duration-200 z-10"
                aria-label="Scroll categories left"
                data-testid="filter-scroll-left"
              >
                <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>

              {/* Right scroll arrow */}
              <button
                type="button"
                onClick={() => scrollFilterBar(280)}
                className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-white/10 backdrop-blur-md text-white hover:bg-white hover:text-[#1a1a1a] border border-white/20 hover:border-white shadow-lg flex items-center justify-center transition-all duration-200 z-10"
                aria-label="Scroll categories right"
                data-testid="filter-scroll-right"
              >
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>

              {/* Edge fade gradients for elegance */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute right-0 top-0 h-full w-12 sm:w-16 bg-gradient-to-l from-[#1a1a1a] via-[#1a1a1a]/60 to-transparent"
              />
              <div
                aria-hidden="true"
                className="pointer-events-none absolute left-0 top-0 h-full w-12 sm:w-16 bg-gradient-to-r from-[#1a1a1a] via-[#1a1a1a]/60 to-transparent"
              />
            </div>
          </section>

          {/* Products Grid */}
          <section className="py-6 sm:py-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between mb-4">
                <p className="text-stone-600 text-sm" data-testid="products-count">
                  <span className="font-medium text-[#1a1a1a]">
                    {filteredProducts.length}
                  </span>{' '}
                  {filteredProducts.length === 1 ? 'product' : 'products'}
                  {totalPages > 1 && (
                    <span className="text-stone-400">
                      {' '}· Page {safePage} of {totalPages}
                    </span>
                  )}
                </p>
              </div>

              {loading ? (
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div
                      key={i}
                      className="bg-white rounded-xl h-[280px] sm:h-[380px] animate-pulse"
                    />
                  ))}
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-stone-500">
                    No products found in this category.
                  </p>
                  <Button
                    variant="outline"
                    className="mt-4 rounded-full"
                    onClick={() => handleCategoryChange('')}
                    data-testid="empty-back-categories-btn"
                  >
                    Back to Categories
                  </Button>
                </div>
              ) : (
                <>
                  <div
                    className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6"
                    data-testid="products-grid"
                  >
                    {paginatedProducts.map((product, idx) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        onQuickAdd={handleQuickAdd}
                        priority={idx < 2}
                      />
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div
                      className="flex items-center justify-center gap-2 mt-10 sm:mt-14"
                      data-testid="pagination"
                    >
                      <button
                        onClick={() => handlePageChange(safePage - 1)}
                        disabled={safePage === 1}
                        className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-stone-300 text-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#1a1a1a] transition-all"
                        data-testid="pagination-prev"
                        aria-label="Previous page"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>

                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`h-10 min-w-[2.5rem] px-3 inline-flex items-center justify-center rounded-full font-['Cormorant_Garamond'] font-bold text-base transition-all ${
                            page === safePage
                              ? 'bg-[#1a1a1a] text-white shadow-md'
                              : 'border border-stone-300 text-stone-700 hover:bg-stone-100'
                          }`}
                          data-testid={`pagination-page-${page}`}
                        >
                          {page}
                        </button>
                      ))}

                      <button
                        onClick={() => handlePageChange(safePage + 1)}
                        disabled={safePage === totalPages}
                        className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-stone-300 text-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#1a1a1a] transition-all"
                        data-testid="pagination-next"
                        aria-label="Next page"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        </>
      )}

      {/* Info Bar */}
      <section className="bg-[#1a1a1a] text-white py-6 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center gap-3 sm:gap-4 text-center">
            <p className="text-stone-300 text-sm sm:text-base">
              <span className="text-[#D4AF37] font-medium">48-hour lead time</span> on all custom orders
            </p>
            <p className="text-stone-300 text-sm sm:text-base">
              <span className="text-[#D4AF37] font-medium">Free collection</span> at 17 Nephin Road, Dublin 7
            </p>
            <p className="text-stone-300 text-sm sm:text-base">
              Use code <span className="font-mono text-[#D4AF37]">MADBRO10</span> for 10% off
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
